// ✅ src/pages/VehicleV2/BatteryTesting.jsx  (UPDATED FULL FILE)
// Backend expected:
//   app.use("/api/batteryCardTesting", batteryTesting);
//
// APIs used:
//   GET  http://nissi-india.com/api/batteryCardTesting/list?batteryCardMainId=..&loginUserId=..&branchId=..
//   POST http://nissi-india.com/api/batteryCardTesting/submit
//
// Routing:
//   navigate("/vehicle/BatteryTesting", { state: { batteryCardMainId: card.id } })
//
// ✅ Updated pattern like BatteryCardMaintenanceListing:
// - common localStorage/sessionStorage header pattern
// - inventory header support
// - useMemo + useCallback pattern
// - loading + pageLoading states
// - same UI structure style
// - batteryCardMainId from state/query/localStorage
// - backend batteryCode shown in Description column
// - submit keeps batteryCode + entered values

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSave, FaUndo, FaArrowLeft, FaBatteryFull } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/batteryCardTesting";

const toText = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v);
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const clean = (v) => String(v ?? "").trim();

const BatteryTesting = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "8";
  const branch =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // ✅ get batteryCardMainId from state/query/localStorage
  const batteryCardMainId = useMemo(() => {
    const fromState = toInt(location?.state?.batteryCardMainId);
    if (fromState) return fromState;

    const sp = new URLSearchParams(location?.search || "");
    const fromQuery = toInt(
      sp.get("batteryCardMainId") || sp.get("batterycardmainid")
    );
    if (fromQuery) return fromQuery;

    return toInt(localStorage.getItem("batteryCardMainId") || 0);
  }, [location]);

  const [rows, setRows] = useState([]);
  const [inputs, setInputs] = useState({});

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const total = rows.length;
  const totalPages = useMemo(() => {
    const t = Math.ceil(total / limit);
    return t > 0 ? t : 1;
  }, [total, limit]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * limit;
    return rows.slice(start, start + limit);
  }, [rows, page, limit]);

  const inputBaseClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const disabledInputClass = `w-full px-3 py-2 border rounded-lg ${
    isDark
      ? "bg-gray-700/20 border-gray-600 text-gray-400"
      : "bg-gray-100 border-gray-300 text-gray-600"
  }`;

  const getRowKey = useCallback((r) => {
    return String(
      r?.batteryCode ??
        r?.fld_batterycode ??
        r?.batteryId ??
        r?.vehicleId ??
        ""
    );
  }, []);

  const mapRow = useCallback((r) => {
    return {
      batteryCardMainId: toInt(
        r?.batteryCardMainId ?? r?.fld_batterycardmainid ?? 0
      ),
      vehicleId: toInt(r?.vehicleId ?? r?.fld_vehicleid ?? 0),
      batteryCode: toText(r?.batteryCode ?? r?.fld_batterycode),
      voltage: toText(r?.voltage ?? r?.fld_voltage),
      cell1: toText(r?.cell1 ?? r?.fld_cell1 ?? "0"),
      cell2: toText(r?.cell2 ?? r?.fld_cell2 ?? "0"),
      cell3: toText(r?.cell3 ?? r?.fld_cell3 ?? "0"),
      cell4: toText(r?.cell4 ?? r?.fld_cell4 ?? "0"),
      cell5: toText(r?.cell5 ?? r?.fld_cell5 ?? "0"),
      cell6: toText(r?.cell6 ?? r?.fld_cell6 ?? "0"),
    };
  }, []);

  /* ------------------------- LIST LOAD ------------------------- */
  const fetchList = useCallback(async () => {
    if (!batteryCardMainId) {
      toast.error("batteryCardMainId not found. Please open from listing page.");
      setRows([]);
      return;
    }

    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/list`, {
        params: {
          batteryCardMainId: toInt(batteryCardMainId, 0),
          loginUserId: toInt(loginUserId, 0),
          branchId: toInt(branchId, 0),
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load Battery Testing list");
      }

      const list = Array.isArray(data?.list) ? data.list.map(mapRow) : [];
      setRows(list);
      setPage(1);

      const nextInputs = {};
      for (const r of list) {
        const key = getRowKey(r);
        if (!key) continue;

        nextInputs[key] = {
          voltage: clean(r?.voltage ?? ""),
          cell1: toText(r?.cell1 ?? "0"),
          cell2: toText(r?.cell2 ?? "0"),
          cell3: toText(r?.cell3 ?? "0"),
          cell4: toText(r?.cell4 ?? "0"),
          cell5: toText(r?.cell5 ?? "0"),
          cell6: toText(r?.cell6 ?? "0"),
        };
      }

      setInputs(nextInputs);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load Battery Testing list"
      );
      setRows([]);
      setInputs({});
    } finally {
      setPageLoading(false);
    }
  }, [
    batteryCardMainId,
    loginUserId,
    branchId,
    commonHeaders,
    mapRow,
    getRowKey,
  ]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  /* ------------------------- INPUT CHANGE ------------------------- */
  const handleCellChange = (rowKey, field, value) => {
    setInputs((prev) => ({
      ...prev,
      [rowKey]: {
        ...(prev[rowKey] || {}),
        [field]: value,
      },
    }));
  };

  /* ------------------------- ACTIONS ------------------------- */
  const handleReset = () => {
    const next = {};
    for (const r of rows) {
      const key = getRowKey(r);
      if (!key) continue;

      next[key] = {
        voltage: "",
        cell1: "0",
        cell2: "0",
        cell3: "0",
        cell4: "0",
        cell5: "0",
        cell6: "0",
      };
    }

    setInputs(next);
    toast.info("Form reset");
  };

  const handleSubmit = async () => {
    if (!batteryCardMainId) {
      toast.error("batteryCardMainId not found.");
      return;
    }

    if (!rows.length) {
      toast.info("No rows to submit");
      return;
    }

    for (let i = 0; i < rows.length; i++) {
      const key = getRowKey(rows[i]);
      const v = inputs[key] || {};
      if (!clean(v.voltage)) {
        toast.error(`Please enter the voltage (row ${i + 1})`);
        return;
      }
    }

    const payloadRows = rows.map((r) => {
      const key = getRowKey(r);
      const v = inputs[key] || {};

      return {
        vehicleId: toInt(r?.vehicleId ?? 0),
        batteryCode: clean(r?.batteryCode ?? ""),
        voltage: clean(v?.voltage),
        cell1: clean(v?.cell1) || "0",
        cell2: clean(v?.cell2) || "0",
        cell3: clean(v?.cell3) || "0",
        cell4: clean(v?.cell4) || "0",
        cell5: clean(v?.cell5) || "0",
        cell6: clean(v?.cell6) || "0",
      };
    });

    try {
      setLoading(true);

      const { data } = await axios.post(
        `${API_BASE}/submit`,
        {
          batteryCardMainId: toInt(batteryCardMainId, 0),
          loginUserId: toInt(loginUserId, 0),
          branchId: toInt(branchId, 0),
          rows: payloadRows,
        },
        { headers: commonHeaders }
      );

      if (!data?.success) {
        throw new Error(data?.message || "Submit failed");
      }

      toast.success(data?.message || "Saved Successfully!");
      await fetchList();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Submit failed"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  /* ------------------------- PAGINATION ------------------------- */
  const goPrev = () => {
    setPage((prev) => Math.max(1, prev - 1));
  };

  const goNext = () => {
    setPage((prev) => Math.min(totalPages, prev + 1));
  };

  const changeLimit = (e) => {
    const nextLimit = toInt(e.target.value, 10);
    setLimit(nextLimit);
    setPage(1);
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BATTERY TESTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
            <div className="flex items-center gap-4 flex-wrap">
              <h1
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Battery Testing
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark
                    ? "bg-gray-800 text-gray-200"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                Main ID : {batteryCardMainId || "-"}
              </span>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleBack}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                }`}
                disabled={loading || pageLoading}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* Table */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {pageLoading || loading ? (
              <div className="text-center py-16">Loading...</div>
            ) : rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaBatteryFull className="w-10 h-10 text-gray-400" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  No Battery Testing Rows Found
                </h3>
                <p className={`${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  No battery rows available for this maintenance card.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-[1100px] w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold" rowSpan={2}>
                        S.No
                      </th>
                      <th className="px-4 py-4 text-center font-semibold" rowSpan={2}>
                        Description
                      </th>
                      <th className="px-4 py-4 text-center font-semibold" colSpan={1}>
                        Voltmeter
                      </th>
                      <th className="px-4 py-4 text-center font-semibold" colSpan={6}>
                        Hydro Meter Test / Specific Gravity Test
                      </th>
                    </tr>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">
                        Voltage
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">Cell 1</th>
                      <th className="px-4 py-4 text-center font-semibold">Cell 2</th>
                      <th className="px-4 py-4 text-center font-semibold">Cell 3</th>
                      <th className="px-4 py-4 text-center font-semibold">Cell 4</th>
                      <th className="px-4 py-4 text-center font-semibold">Cell 5</th>
                      <th className="px-4 py-4 text-center font-semibold">Cell 6</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {pagedRows.map((r, idx) => {
                      const key = getRowKey(r);
                      const v = inputs[key] || {};
                      const description = clean(r?.batteryCode ?? "");

                      return (
                        <tr
                          key={key || idx}
                          className={`transition-colors duration-200 ${
                            isDark ? "bg-gray-900" : "bg-white"
                          }`}
                        >
                          <td
                            className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {(page - 1) * limit + idx + 1}
                          </td>

                          <td className="px-4 py-3 text-sm text-center">
                            <input
                              value={description}
                              className={disabledInputClass}
                              disabled
                            />
                          </td>

                          <td className="px-4 py-3 text-sm text-center">
                            <input
                              value={v.voltage ?? ""}
                              onChange={(e) =>
                                handleCellChange(key, "voltage", e.target.value)
                              }
                              className={inputBaseClass}
                              disabled={loading || pageLoading}
                            />
                          </td>

                          {["cell1", "cell2", "cell3", "cell4", "cell5", "cell6"].map(
                            (field) => (
                              <td
                                key={field}
                                className="px-4 py-3 text-sm text-center"
                              >
                                <input
                                  value={v[field] ?? ""}
                                  onChange={(e) =>
                                    handleCellChange(key, field, e.target.value)
                                  }
                                  className={inputBaseClass}
                                  disabled={loading || pageLoading}
                                />
                              </td>
                            )
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination + Actions */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-6">
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={goPrev}
                className={`px-3 py-2 rounded border ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
                disabled={page <= 1 || loading || pageLoading}
              >
                Prev
              </button>

              <div className={`${isDark ? "text-gray-200" : "text-gray-800"} text-sm`}>
                Page <b>{page}</b> / <b>{totalPages}</b>
              </div>

              <button
                onClick={goNext}
                className={`px-3 py-2 rounded border ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
                disabled={page >= totalPages || loading || pageLoading}
              >
                Next
              </button>

              <select
                value={limit}
                onChange={changeLimit}
                disabled={loading || pageLoading}
                className={`px-2 py-2 rounded border ${
                  isDark
                    ? "bg-gray-800 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-800"
                }`}
              >
                {[10, 20, 50, 100].map((x) => (
                  <option key={x} value={x}>
                    {x} / page
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3 flex-wrap">
              <button
                type="button"
                onClick={handleSubmit}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${
                  loading || pageLoading ? "opacity-70 cursor-not-allowed" : ""
                }`}
                disabled={loading || pageLoading}
              >
                <FaSave />
                Submit
              </button>

              <button
                type="button"
                onClick={handleReset}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${
                  isDark
                    ? "bg-red-700 hover:bg-red-600 text-white"
                    : "bg-red-600 hover:bg-red-700 text-white"
                } ${loading || pageLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                disabled={loading || pageLoading}
              >
                <FaUndo />
                Reset
              </button>
            </div>
          </div>

          <div className={`mt-8 pt-6 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <p className={`text-sm text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              INFRA SCREEN : BATTERY TESTING
            </p>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default BatteryTesting;